/**
 * Sección "Datos personales".
 *
 *   - Identidad: edita first_name + last_name (PATCH /me/profile).
 *   - Correo:   inline change con OTP de 6 dígitos al nuevo correo.
 *   - Teléfono: inline change con password (+ TOTP si MFA on).
 *   - Foto:     placeholder (avatar upload llega en próximo hito).
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { CheckCircle2, MailWarning, Pencil, Phone, X } from 'lucide-react'

import {
  ActionButton,
  DefList,
  DefRow,
  FieldRow,
  InlineAlert,
  SettingsSection,
  TextInput,
} from '@/components/ui/settings-primitives'
import { COUNTRIES, PhoneInput } from '@/components/ui/phone-input'
import {
  useChangePhone,
  useEmailChangeStart,
  useEmailChangeVerify,
  useUpdateProfile,
} from '@/hooks/api/use-profile'
import type { ProfileResponse } from '@/lib/api'

export function ProfileDataSection({ profile }: { profile: ProfileResponse }) {
  return (
    <div className="space-y-6">
      <IdentitySection profile={profile} />
      <ContactSection profile={profile} />
      <AvatarSection profile={profile} />
    </div>
  )
}

// ─── Sub-sección: identidad (nombres) ──────────────────────────────

function IdentitySection({ profile }: { profile: ProfileResponse }) {
  const [firstName, setFirstName] = useState(profile.first_name)
  const [lastName, setLastName] = useState(profile.last_name)
  const dirty = firstName !== profile.first_name || lastName !== profile.last_name
  const update = useUpdateProfile()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await update.mutateAsync({ first_name: firstName.trim(), last_name: lastName.trim() })
      toast.success('Datos actualizados')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'No pudimos guardar los cambios')
    }
  }
  const onReset = () => {
    setFirstName(profile.first_name)
    setLastName(profile.last_name)
  }

  return (
    <SettingsSection
      title="Identidad"
      description="Tu nombre tal como aparece en facturas y comunicaciones."
      footer={
        <>
          <ActionButton variant="ghost" onClick={onReset} disabled={!dirty || update.isPending}>
            Descartar
          </ActionButton>
          <ActionButton
            type="submit"
            form="profile-data-form"
            disabled={!dirty}
            loading={update.isPending}
          >
            Guardar cambios
          </ActionButton>
        </>
      }
    >
      <form id="profile-data-form" onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        <FieldRow label="Nombres">
          <TextInput
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={200}
            required
            autoComplete="given-name"
          />
        </FieldRow>
        <FieldRow label="Apellidos">
          <TextInput
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={200}
            required
            autoComplete="family-name"
          />
        </FieldRow>
      </form>
    </SettingsSection>
  )
}

// ─── Sub-sección: contacto (correo + teléfono) ────────────────────

function ContactSection({ profile }: { profile: ProfileResponse }) {
  const [editing, setEditing] = useState<'email' | 'phone' | null>(null)

  // Email change exige password o MFA por ser identidad recuperable.
  // Phone change es laxo: cualquier usuario autenticado puede cambiarlo
  // (la verificación real llega por SMS/WhatsApp, próximamente).
  const canEditEmail = profile.has_password || profile.totp_enabled
  const canEditPhone = true

  return (
    <SettingsSection
      title="Contacto"
      description="Cambiar el correo requiere verificarlo con un código al nuevo correo. El teléfono se actualiza desde aquí (la verificación SMS/WhatsApp llega pronto)."
    >
      {!canEditEmail && (
        <div className="mb-5">
          <InlineAlert variant="info">
            Tu cuenta usa solo Google/Microsoft. Para cambiar el correo, configura una
            contraseña en la sección "Contraseña" o activa la autenticación de dos
            factores (2FA). El teléfono sí lo puedes cambiar sin esos pasos extra.
          </InlineAlert>
        </div>
      )}
      <DefList>
        <DefRow term="Correo electrónico">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5">
              {profile.email}
              {profile.email_verified ? (
                <CheckCircle2 className="h-4 w-4 text-success-fg" aria-label="verificado" />
              ) : (
                <MailWarning className="h-4 w-4 text-warning-fg" aria-label="sin verificar" />
              )}
            </span>
            {canEditEmail && editing !== 'email' && (
              <button
                type="button"
                onClick={() => setEditing('email')}
                className="rounded p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-secondary"
                aria-label="Editar correo"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </DefRow>
        <DefRow term="Teléfono">
          <div className="flex items-center gap-2">
            {profile.phone ? (
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-fg-muted" />
                {formatPhone(profile.phone_country_code, profile.phone)}
                {profile.phone_verified && (
                  <CheckCircle2 className="h-4 w-4 text-success-fg" aria-label="verificado" />
                )}
              </span>
            ) : (
              <span className="text-fg-muted">Sin registrar</span>
            )}
            {canEditPhone && editing !== 'phone' && (
              <button
                type="button"
                onClick={() => setEditing('phone')}
                className="rounded p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-secondary"
                aria-label="Editar teléfono"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </DefRow>
        <DefRow term="País">{profile.country_iso}</DefRow>
        <DefRow term="Cuenta creada">
          {new Date(profile.created_at).toLocaleDateString('es-CO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </DefRow>
      </DefList>

      {editing === 'email' && (
        <div className="mt-5 rounded-lg border border-border bg-bg-app/40 p-4">
          <EmailChangeFlow profile={profile} onClose={() => setEditing(null)} />
        </div>
      )}
      {editing === 'phone' && (
        <div className="mt-5 rounded-lg border border-border bg-bg-app/40 p-4">
          <PhoneChangeFlow profile={profile} onClose={() => setEditing(null)} />
        </div>
      )}
    </SettingsSection>
  )
}

// ─── Email change flow (start → OTP → verify) ─────────────────────

function EmailChangeFlow({
  profile,
  onClose,
}: {
  profile: ProfileResponse
  onClose: () => void
}) {
  const currentEmail = profile.email
  const [stage, setStage] = useState<'request' | 'verify'>('request')
  const [newEmail, setNewEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [code, setCode] = useState('')
  const [maskedEmail, setMaskedEmail] = useState<string>('')

  const start = useEmailChangeStart()
  const verify = useEmailChangeVerify()

  const onStart = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = newEmail.trim().toLowerCase()
    if (trimmed === currentEmail.toLowerCase()) {
      toast.error('El nuevo correo es idéntico al actual')
      return
    }
    try {
      const resp = await start.mutateAsync({
        new_email: trimmed,
        password: profile.has_password ? password : null,
        totp_code: profile.totp_enabled ? totp.trim() : null,
      } as Parameters<typeof start.mutateAsync>[0])
      setMaskedEmail(resp.new_email_masked)
      setStage('verify')
      setPassword('')
      setTotp('')
      toast.success(resp.message)
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[email-change-start] error:', err)
      const e = err as {
        response?: { status?: number; data?: { detail?: string | { message?: string } } }
      }
      const detail = e?.response?.data?.detail
      const msg =
        typeof detail === 'string'
          ? detail
          : typeof detail === 'object' && detail?.message
          ? detail.message
          : 'No pudimos iniciar el cambio'
      toast.error(msg)
    }
  }

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const resp = await verify.mutateAsync(code.trim())
      toast.success(resp.message, { duration: 6000 })
      onClose()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'Código incorrecto')
    }
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg-primary">
          {stage === 'request' ? 'Cambiar correo electrónico' : 'Confirma el nuevo correo'}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-secondary"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {stage === 'request' ? (
        <form onSubmit={onStart} className="space-y-3">
          <FieldRow label="Nuevo correo electrónico">
            <TextInput
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </FieldRow>
          {profile.has_password && (
            <FieldRow
              label="Contraseña actual"
              hint="Necesaria para confirmar que eres tú quien solicita el cambio."
            >
              <TextInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </FieldRow>
          )}
          {profile.totp_enabled && (
            <FieldRow
              label="Código MFA de 6 dígitos"
              hint="Confirmación adicional con tu app de autenticación."
            >
              <TextInput
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totp}
                onChange={(e) => setTotp(e.target.value)}
                required
              />
            </FieldRow>
          )}
          <ActionButton
            type="submit"
            disabled={
              !newEmail ||
              (profile.has_password && !password) ||
              (profile.totp_enabled && totp.length < 6)
            }
            loading={start.isPending}
          >
            Enviar código al nuevo correo
          </ActionButton>
        </form>
      ) : (
        <form onSubmit={onVerify} className="space-y-3">
          <InlineAlert variant="info">
            Te enviamos un código a <strong>{maskedEmail}</strong>. Ingrésalo aquí. Expira
            en 10 minutos.
          </InlineAlert>
          <FieldRow label="Código de 6 dígitos">
            <TextInput
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
          </FieldRow>
          <div className="flex gap-2">
            <ActionButton variant="ghost" onClick={() => setStage('request')}>
              ← Volver
            </ActionButton>
            <ActionButton type="submit" disabled={code.length < 6} loading={verify.isPending}>
              Confirmar nuevo correo
            </ActionButton>
          </div>
        </form>
      )}
    </div>
  )
}

// ─── Phone change flow ─────────────────────────────────────────────

function PhoneChangeFlow({
  profile,
  onClose,
}: {
  profile: ProfileResponse
  onClose: () => void
}) {
  // Initial state: existing values when present, sensible default otherwise.
  // We default the dial code to +57 (Colombia) since that's our home market.
  const [cc, setCc] = useState(profile.phone_country_code || '+57')
  const [phone, setPhone] = useState((profile.phone || '').replace(/\D/g, ''))
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')

  const change = useChangePhone()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (profile.has_password && !password) {
      toast.error('Debes ingresar tu contraseña actual')
      return
    }
    if (profile.totp_enabled && totp.trim().length < 6) {
      toast.error('Ingresa tu código MFA de 6 dígitos')
      return
    }
    try {
      await change.mutateAsync({
        phone: phone.trim() || null,
        phone_country_code: phone ? cc : null, // null both fields if clearing
        password: profile.has_password ? password : null,
        totp_code: profile.totp_enabled ? totp.trim() : null,
      })
      toast.success('Teléfono actualizado')
      onClose()
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('[change-phone] error:', err)
      const e = err as {
        response?: { status?: number; data?: { detail?: string | { message?: string } } }
      }
      const detail = e?.response?.data?.detail
      const status = e?.response?.status
      const msg =
        typeof detail === 'string'
          ? detail
          : typeof detail === 'object' && detail?.message
          ? detail.message
          : status === 401
          ? 'Contraseña o código MFA incorrecto.'
          : status === 422
          ? 'Revisa que el número tenga sólo dígitos y el código país sea válido.'
          : 'No pudimos actualizar el teléfono.'
      toast.error(msg)
    }
  }

  const validCC = COUNTRIES.some((c) => c.code === cc)

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg-primary">Cambiar teléfono</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-secondary"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <FieldRow
          label="Teléfono"
          hint="Selecciona el país y escribe sólo el número (sin guiones ni espacios)."
        >
          <PhoneInput
            countryCode={cc}
            phone={phone}
            onChange={({ countryCode, phone: nextPhone }) => {
              setCc(countryCode)
              setPhone(nextPhone)
            }}
            autoFocus
          />
        </FieldRow>
        {profile.has_password && (
          <FieldRow
            label="Contraseña actual"
            hint="Cambiar tu teléfono requiere confirmar tu identidad."
          >
            <TextInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </FieldRow>
        )}
        {profile.totp_enabled && (
          <FieldRow label="Código MFA de 6 dígitos">
            <TextInput
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              required
            />
          </FieldRow>
        )}
        <InlineAlert variant="info">
          Marcaremos el nuevo número como <em>no verificado</em>. La verificación por
          WhatsApp/SMS llegará pronto.
        </InlineAlert>
        <ActionButton
          type="submit"
          disabled={
            (profile.has_password && !password) ||
            !validCC ||
            (profile.totp_enabled && totp.length < 6)
          }
          loading={change.isPending}
        >
          Guardar teléfono
        </ActionButton>
      </form>
    </div>
  )
}

// ─── Pretty-print stored phone ─────────────────────────────────────

function formatPhone(cc: string | null, phone: string | null): string {
  const digits = (phone ?? '').replace(/\D/g, '')
  if (!digits) return '—'
  if (!cc) return digits
  return `${cc} ${digits}`
}

// ─── Avatar (placeholder hasta upload) ─────────────────────────────

function AvatarSection({ profile }: { profile: ProfileResponse }) {
  return (
    <SettingsSection
      title="Foto de perfil"
      description="Hoy mostramos tus iniciales como avatar. La carga de imagen llegará pronto."
    >
      <div className="flex items-center gap-4">
        <div
          aria-hidden
          className="flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-rose-500 font-mono text-2xl font-bold text-white shadow-sm"
        >
          {profile.initials}
        </div>
        <p className="text-sm text-fg-secondary">
          Tu avatar se genera a partir del nombre. Cambia tus iniciales editando "Identidad".
        </p>
      </div>
    </SettingsSection>
  )
}
